import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Barcode from 'react-barcode';
import QRCode from 'qrcode.react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import AssortiTable from './AssortiTable';
import ImageModal from './ImageModal';
import { fetchApi, API_URL } from '../config/api';
import { API_URL as ConstantsAPI_URL } from '../config/constants';

interface OrderDetail {
  orderNumber: string;
  orderDate: string;
  terminDate: string;
  paymentType: string;
  manufacturerCode: string;
  manufacturerCompany: string;
  modelName: string;
  merchantSubGroup: string;
  buyer: string;
  season: string;
  productDescription: string;
  yiInspectionDate: string;
  brand: string;
  deliveryLocation: string;
  totalOrderQuantity: number;
  totalAmount: number;
  kdvRate: number;
  kdvTotal: number;
  optionPrice: string;
  productImage: string | null;
  status: string;
  assortiColumns: { name: string; order: number }[];
  assortiValues: { columnName: string; value: number }[];
}

const OrderDetail: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const INITIAL_CODE = '868138';

  const calculateCheckDigit = (code: string): number => {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const digit = parseInt(code[i]);
      sum += (i % 2 === 0) ? digit : digit * 3;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit;
  };

  const generateBarcode = (orderNumber: string): string => {
    const code = orderNumber.slice(-6);
    const code12 = INITIAL_CODE + code;
    const checkDigit = calculateCheckDigit(code12);
    return code12 + checkDigit;
  };

  useEffect(() => {
    fetchOrderDetail();
  }, [orderNumber]);

  const fetchOrderDetail = async () => {
    try {
      const data = await fetchApi(`/orders/${orderNumber}`);
      setOrder(data);
    } catch (error) {
      console.error('Sipariş detaylarını getirme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (newStatus: string) => {
    try {
      const updatedOrder = await fetchApi(`/orders/${orderNumber}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      setOrder(updatedOrder);
    } catch (error) {
      console.error('Sipariş durumu güncelleme hatası:', error);
      alert('Sipariş durumu güncellenirken bir hata oluştu');
    }
  };

  const handleDownload = async () => {
    if (!order) return;
    
    try {
      const response = await fetch(`${API_URL}/orders/${order.orderNumber}/pdf`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error('PDF oluşturulurken bir hata oluştu');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `siparis-${order.orderNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF indirme hatası:', error);
      alert('PDF indirilirken bir hata oluştu');
    }
  };

  const downloadPNG = async () => {
    if (!order) return;
    
    try {
      const response = await fetch(`${API_URL}/orders/${order.orderNumber}/png`, {
        method: 'GET',
        headers: {
          'Accept': 'image/png'
        }
      });

      if (!response.ok) {
        throw new Error('PNG oluşturulurken bir hata oluştu');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `siparis-${order.orderNumber}.png`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PNG indirme hatası:', error);
      alert('PNG indirilirken bir hata oluştu');
    }
  };

  const handlePreview = async () => {
    if (!order) return;
    
    try {
      const response = await fetch(`${API_URL}/orders/${order.orderNumber}/pdf`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error('PDF önizleme oluşturulurken bir hata oluştu');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('PDF önizleme hatası:', error);
      alert('PDF önizleme sırasında bir hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Sipariş bulunamadı</div>
      </div>
    );
  }

  const barcodeValue = generateBarcode(order.orderNumber);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
      <div ref={contentRef} className="max-w-6xl mx-auto">
        {/* Üst Bilgi */}
        <div className="bg-blue-50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-blue-700">
              Sipariş detaylarını görüntülüyorsunuz. Barkod ve QR kodlarını indirebilir veya yazdırabilirsiniz.
            </p>
          </div>
        </div>

        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-md p-8 mb-4 transform transition-all duration-300 hover:shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Sipariş Detayları
              </h2>
              <p className="text-gray-500 mt-2">Sipariş #{order?.orderNumber}</p>
            </div>
            <div className="bg-blue-50 px-4 py-2 rounded-lg">
              <div className="text-sm font-medium text-blue-600">
                {order && new Date(order.orderDate).toLocaleDateString('tr-TR', { 
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>

        {order && (
          <>
            {/* Temel Bilgiler */}
            <div className="bg-white rounded-2xl shadow-md p-8 mb-4 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Temel Bilgiler</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Sipariş Numarası</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.orderNumber}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Sipariş Tarihi</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {new Date(order.orderDate).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Termin Tarihi</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {new Date(order.terminDate).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Ödeme Tipi</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.paymentType}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Durum</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    <span className={`px-2 py-1 rounded-full text-sm font-medium
                      ${order.status === 'Beklemede' ? 'bg-yellow-100 text-yellow-800' : 
                        order.status === 'Üretimde' ? 'bg-blue-100 text-blue-800' : 
                        order.status === 'Tamamlandı' ? 'bg-green-100 text-green-800' : 
                        'bg-gray-100 text-gray-800'}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Barkod ve QR Kod */}
            <div className="bg-white rounded-2xl shadow-md p-8 mb-4 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Barkod ve QR Kod</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex flex-col items-center p-6 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Barkod</h3>
                  <div className="bg-white p-4">
                    <Barcode
                      value={generateBarcode(order.orderNumber)}
                      width={2}
                      height={100}
                      displayValue={true}
                      margin={10}
                      background="#FFFFFF"
                      lineColor="#000000"
                      fontSize={14}
                    />
                  </div>
                </div>
                <div className="flex flex-col items-center p-6 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">QR Kod</h3>
                  <div className="bg-white p-4">
                    <QRCode
                      value={`http://localhost:3000/order-detail/${order.orderNumber}`}
                      size={150}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Üretici Bilgileri */}
            <div className="bg-white rounded-2xl shadow-md p-8 mb-4 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Üretici Bilgileri</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Üretici Firma</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.manufacturerCompany}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Üretici Kodu</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.manufacturerCode}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Model Adı</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.modelName}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Sezon</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.season}
                  </div>
                </div>
              </div>
            </div>

            {/* Ürün Detayları */}
            <div className="bg-white rounded-2xl shadow-md p-8 mb-4 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Ürün Detayları</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Mal Tanımı</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.productDescription}
                  </div>
                </div>
                {order.productImage && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ürün Görseli</label>
                    <div 
                      className="w-48 h-48 rounded-lg overflow-hidden border border-gray-200 cursor-pointer transition-transform hover:scale-[1.02]"
                      onClick={() => setIsImageModalOpen(true)}
                    >
                      <img
                        src={`${API_URL}${order.productImage}`}
                        alt={order.modelName}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fiyatlandırma */}
            <div className="bg-white rounded-2xl shadow-md p-8 mb-4 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Fiyatlandırma</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Toplam Miktar</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.totalOrderQuantity}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Toplam Tutar</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.totalAmount.toLocaleString('tr-TR')} ₺
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">KDV Oranı</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    %{order.kdvRate}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">KDV Tutarı</label>
                  <div className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-gray-50">
                    {order.kdvTotal.toLocaleString('tr-TR')} ₺
                  </div>
                </div>
              </div>
            </div>

            {/* Asorti Tablosu */}
            <div className="bg-white rounded-2xl shadow-md p-8 mb-4 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-100 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Asorti Sipariş</h3>
                </div>
                <div className="bg-indigo-50 px-4 py-2 rounded-lg">
                  <div className="text-sm font-medium text-indigo-600">
                    Toplam Miktar: {order.totalOrderQuantity}
                  </div>
                </div>
              </div>
              <AssortiTable
                columns={order.assortiColumns}
                values={order.assortiValues}
                onColumnsChange={() => {}}
                onValuesChange={() => {}}
                readOnly={true}
              />
            </div>

            {/* İşlem Butonları */}
            <div className="bg-white rounded-2xl shadow-md p-8 mb-4 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800">İşlemler</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 action-buttons">
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {downloading ? 'İndiriliyor...' : 'PDF İndir'}
                </button>

                <button
                  onClick={downloadPNG}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl hover:from-green-700 hover:to-teal-700 transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  PNG İndir
                </button>

                <button
                  onClick={handlePreview}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  PDF Önizle
                </button>
              </div>
            </div>

            {/* Alt Butonlar */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                onClick={() => navigate('/orders')}
                className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-300"
              >
                Siparişlere Dön
              </button>
              <button
                onClick={() => navigate('/create-order')}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300"
              >
                Yeni Sipariş Oluştur
              </button>
            </div>
          </>
        )}
      </div>

      {/* Image Modal */}
      {order?.productImage && (
        <ImageModal
          imageUrl={`${API_URL}${order.productImage}`}
          alt={order.modelName}
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}
    </div>
  );
};

export default OrderDetail; 